import React, {
  useState,
  useEffect,
  useRef,
} from "react";
import {
  AlertCircle,
  Info,
  Sparkles,
  RefreshCw,
  Save,
  Download,
  BrainCircuit,
  FileText,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "../../common/Button";
import WorkshopExportMenu from "../../common/WorkshopExportMenu";
import ThreatCategories, { THREAT_CATEGORIES } from "./ThreatCategories";
import CategorySourcesTable from "./CategorySourcesTable";
import GuideModal from "./GuideModal";
import InlineAiSuggestions from "../../common/InlineAiSuggestions";
import { useAuth } from '../../../context/AuthContext';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import useAtelier2Store from '../../../stores/useAtelier2Store';
import { useBroadcast } from '../../../hooks/useBroadcast';
import { getAiSourcesRisqueSuggestions } from "../../../services/sourcesRisqueService";
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  updateToast,
} from "../../../utils/toastUtils";
import { createIdGenerator } from "../../../utils/idGenerator";
import { useSafeSave } from "../../../hooks/useSafeSave";
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';

const Activite1 = (props) => {
  const { t, i18n } = useTranslation();
  const { companyAiEnabled } = useAuth();
  const sourcesRisque = useAtelier2Store((s) => s.sourcesRisque);
  const isDataLoaded = useAtelier2Store((s) => s.isDataLoaded);
  const updateSourcesRisque = useAtelier2Store((s) => s.setSourcesRisque);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [showAiSuggestionsInline, setShowAiSuggestionsInline] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [selectedAiSuggestions, setSelectedAiSuggestions] = useState([]);

  const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const atelier2Store = useAtelier2Store();
  const broadcastUpdate = useBroadcast();
  const currentAnalysis = analysisStore.currentAnalysis;
  const currentContextData = atelier1Store.contextData;
  const currentBusinessValues = atelier1Store.businessValues;
  const categoriesInitializedRef = useRef(false);

  const {
    markDataLoaded,
    markModified,
    canAutoSave,
    clearModified,
    useFormChangeDetector,
  } = useSafeSave("Activite1");
  const formChangeRef = useFormChangeDetector();

  // Handle silent save without toasts
  const handleSilentSave = async () => {
    if (!canAutoSave()) return "skipped";

    if (currentAnalysis?.id) {
      try {
        console.log("Activite1 - Performing silent save of sources de risque");
        await useAtelier2Store.getState().saveSourcesDeRisque(currentAnalysis.id, sourcesRisque, null, broadcastUpdate);
        clearModified();
        console.log("Activite1 - Silent save successful");
        return true;
      } catch (error) {
        console.error("Activite1 - Silent save error:", error);
        return false;
      }
    }
    return false;
  };

  useSilentSaveListener('atelier2-activite1', handleSilentSave);

  // Create ID generator instance for this analysis
  const idGen = currentAnalysis?.id
    ? createIdGenerator({ analysisId: currentAnalysis.id })
    : null;

  // Initialize selected categories from store data once loaded
  useEffect(() => {
    if (isDataLoaded && sourcesRisque.length > 0 && !categoriesInitializedRef.current) {
      categoriesInitializedRef.current = true;
      const uniqueCategories = [
        ...new Set(
          sourcesRisque.map((source) => source.category).filter(Boolean),
        ),
      ];
      setSelectedCategories(uniqueCategories);
      markDataLoaded();
    }
  }, [isDataLoaded, sourcesRisque, markDataLoaded]);

  // Load threat category definitions (built-ins + custom) into the store.
  // ponytail: ThreatCategories reloads defensively, but calling here guarantees
  // children (SourcesRisques, CategorySourcesTable) see customs on first render.
  useEffect(() => {
    if (currentAnalysis?.id) {
      atelier2Store.loadThreatCategories(currentAnalysis.id).catch((e) => {
        console.error('[Activite1] loadThreatCategories failed:', e);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAnalysis?.id]);

  // Handle AI suggestions
  const handleGetAiSuggestions = async (isRequestingMore = false) => {
    if (!currentAnalysis?.id) {
      showErrorToast(t("workshop2.activity1.errors.noAnalysisSelected"));
      return;
    }

    setIsLoadingAi(true);
    if (!isRequestingMore) {
      setShowAiSuggestionsInline(true);
      setAiSuggestions([]); // Clear previous suggestions on a new request
      setSelectedAiSuggestions([]);
    }

    const toastId = showLoadingToast(
      isRequestingMore
        ? t("workshop2.activity1.aiAssistant.generatingMoreSuggestions")
        : t("workshop2.activity1.aiAssistant.generatingSuggestions"),
    );

    try {
      const existingSourceNames = sourcesRisque.map((source) => source.name);
      if (isRequestingMore) {
        existingSourceNames.push(...aiSuggestions.map((s) => s.name));
      }

      const threatCategories = selectedCategories
        .map((categoryId) => {
          const category = THREAT_CATEGORIES.find((c) => c.id === categoryId);
          return category ? category.id : null;
        })
        .filter(Boolean);

      const businessAssets =
        currentBusinessValues?.flatMap((bv) => bv.supportAssets || []) || [];

      const cleanContext = (items) =>
        items.map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
        }));

      const response = await getAiSourcesRisqueSuggestions({
        analysisId: currentAnalysis.id,
        analysisName: currentAnalysis.name,
        analysisDescription: currentAnalysis.description || "",
        organizationContext: currentAnalysis.organizationContext || "",
        threatCategories,
        existingSourceNames,
        businessValues: cleanContext(currentBusinessValues || []),
        businessAssets: cleanContext(businessAssets),
        requestMore: isRequestingMore,
        language: i18n.language || "fr", // Add language parameter
      });

      if (response.success && response.data.suggestions) {
        const newSuggestions = response.data.suggestions;
        setAiSuggestions((prev) =>
          isRequestingMore ? [...prev, ...newSuggestions] : newSuggestions,
        );
        updateToast(
          toastId,
          t("workshop2.activity1.aiAssistant.suggestionsGenerated"),
          "success",
        );
      } else {
        throw new Error(t("workshop2.activity1.errors.invalidApiResponse"));
      }
    } catch (error) {
      console.error("Error getting AI suggestions:", error);
      updateToast(
        toastId,
        t("workshop2.activity1.errors.aiGenerationError"),
        "error",
      );
      if (!isRequestingMore) {
        setShowAiSuggestionsInline(false);
      }
    } finally {
      setIsLoadingAi(false);
    }
  };

  // Handle requesting more suggestions
  const handleGetMoreSuggestions = () => {
    handleGetAiSuggestions(true);
  };

  // Handle selecting/deselecting AI suggestions
  const handleToggleAiSuggestion = (suggestion) => {
    setSelectedAiSuggestions((prev) => {
      const isSelected = prev.some((s) => s.name === suggestion.name);
      return isSelected
        ? prev.filter((s) => s.name !== suggestion.name)
        : [...prev, suggestion];
    });
  };

  // Handle adding selected AI suggestions to the main list

  const handleAddAiSuggestions = () => {
    if (selectedAiSuggestions.length === 0) {
      showErrorToast(t("workshop2.activity1.errors.noSuggestionSelected"));
      return;
    }

    // Use forEach to avoid "before initialization" error
    const newSources = [];

    selectedAiSuggestions.forEach((suggestion) => {
      // Debug: Check if idGen is available
      if (!idGen) {
        console.warn(
          "⚠️ ID Generator not available. currentAnalysis:",
          currentAnalysis,
        );
      }

      const newSource = {
        // Generate new ID first
        id: idGen
          ? idGen.generateNext("sourceRisque", [
              ...sourcesRisque,
              ...newSources,
            ])
          : `source-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,

        // Basic fields (match manual source exactly)
        name: suggestion.name || "",
        description: suggestion.description || "",

        // ObjectifVise fields
        objectifVise:
          suggestion.objectifVise ||
          suggestion.objectifViseCategory ||
          "confidentialite",
        objectifViseCategory: suggestion.objectifViseCategory || "",

        // Capacity fields (normalize AI field names to match manual)
        motivation: suggestion.motivation || suggestion.intent || "faible",
        activite: suggestion.activite || suggestion.activity || "faible",
        ressources:
          suggestion.ressources ||
          suggestion.resources ||
          suggestion.capability ||
          "faibles",

        // Category
        category: suggestion.category || "",

        // Timestamp
        createdAt: new Date().toISOString(),
      };

      // Debug: Log the generated source
      console.log("✅ Generated AI source:", {
        id: newSource.id,
        name: newSource.name,
        fields: Object.keys(newSource),
      });

      newSources.push(newSource);
    });

    updateSourcesRisque((prev) => [...prev, ...newSources]);

    // Automatically select the categories of the added suggestions
    const newCategories = newSources
      .map((source) => source.category)
      .filter(Boolean); // Filter out empty categories

    setSelectedCategories((prev) => {
      // Create a Set to ensure uniqueness
      const uniqueInputCategories = new Set([...prev, ...newCategories]);
      return Array.from(uniqueInputCategories);
    });

    setSelectedAiSuggestions([]); // Clear selections
    setShowAiSuggestionsInline(false); // Close the inline panel

    showSuccessToast(
      t("workshop2.activity1.success.sourcesAdded", {
        count: newSources.length,
      }),
    );
    markModified();
  };

  // Triggers the save event for the parent component to handle
  const handleSave = async () => {
    console.log("Dispatching save-sources-risque event");
    markModified(); // Just in case, to allow the save to pass through if it delegates to handleSilentSave
    window.dispatchEvent(new CustomEvent("save-sources-risque"));

    // ponytail: also persist threat categories (selection, customs, hidden). The auto-save in
    // ThreatCategories should already have done this, but routing through the global Save button
    // covers the user's mental model where "Save" = "persist everything on this page".
    if (currentAnalysis?.id) {
      try {
        const state = useAtelier2Store.getState();
        await state.saveThreatCategories(currentAnalysis.id, {
          selected: selectedCategories,
          customCategories: state.customCategories,
          hidden: state.hiddenCategories
        });
      } catch (e) {
        console.error('[Activite1] saveThreatCategories on global Save failed:', e);
      }
    }
  };

  // PDF Export Handler
  const handleExportPDF = async () => {
    try {
      // Filter sources to only include those from selected categories (what's visible on page)
      const visibleSources = sourcesRisque.filter((source) =>
        selectedCategories.includes(source.category),
      );

      // Prepare report data with only visible sources
      const categoryDistribution = {};
      visibleSources.forEach((source) => {
        const category = source.category || "non-categorise";
        categoryDistribution[category] =
          (categoryDistribution[category] || 0) + 1;
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: visibleSources.length,
        selectedSources: visibleSources.filter((s) => s.selected).length,
        categoriesCount: Object.keys(categoryDistribution).length,
        categoryDistribution: categoryDistribution,
        sources: visibleSources, // Only visible sources
        customCategories: useAtelier2Store.getState().customCategories,
      };

      // Import PDF generator dynamically
      const { default: SourcesRisquePdfGenerator } =
        await import("../../../utils/sourcesRisquePdfGenerator.js");

      // Create PDF generator instance
      const pdfGenerator = new SourcesRisquePdfGenerator(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      });

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateSourcesRisqueReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EBIOS-RM_Sources-Risque_${currentContextData?.organizationName || "Organisation"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast("Rapport PDF généré avec succès");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      showErrorToast(`Erreur lors de l'export PDF: ${error.message}`);
    }
  };

  // PDF Export Handler (English)
  const handleExportPDFEn = async () => {
    try {
      // Filter sources to only include those from selected categories (what's visible on page)
      const visibleSources = sourcesRisque.filter((source) =>
        selectedCategories.includes(source.category),
      );

      // Prepare report data with only visible sources (same as French)
      const categoryDistribution = {};
      visibleSources.forEach((source) => {
        const category = source.category || "non-categorise";
        categoryDistribution[category] =
          (categoryDistribution[category] || 0) + 1;
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: visibleSources.length,
        selectedSources: visibleSources.filter((s) => s.selected).length,
        categoriesCount: Object.keys(categoryDistribution).length,
        categoryDistribution: categoryDistribution,
        sources: visibleSources, // Only visible sources
        customCategories: useAtelier2Store.getState().customCategories,
      };

      // Import English PDF generator dynamically
      const { default: SourcesRisquePdfGenerator } =
        await import("../../../utils/sourcesRisquePdfGenerator.js");

      // Create PDF generator instance
      const pdfGenerator = new SourcesRisquePdfGenerator(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      }, "en");

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateSourcesRisqueReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EBIOS-RM_Risk-Sources_${currentContextData?.organizationName || "Organization"}_${new Date().toISOString().slice(0, 10)}_EN.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast("PDF Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting PDF (EN):", error);
      showErrorToast(`Error exporting PDF (EN): ${error.message}`);
    }
  };

  // DOCX Export Handler
  const handleExportDocx = async () => {
    try {
      const visibleSources = sourcesRisque.filter((source) =>
        selectedCategories.includes(source.category),
      );

      const categoryDistribution = {};
      visibleSources.forEach((source) => {
        const category = source.category || "non-categorise";
        categoryDistribution[category] =
          (categoryDistribution[category] || 0) + 1;
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: visibleSources.length,
        selectedSources: visibleSources.filter((s) => s.selected).length,
        categoriesCount: Object.keys(categoryDistribution).length,
        categoryDistribution: categoryDistribution,
        sources: visibleSources,
      };

      const { generateSourcesRisqueDocx } =
        await import("../../../utils/sourcesRisqueDocxGenerator.js");

      await generateSourcesRisqueDocx(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      });

      showSuccessToast("Rapport Word généré avec succès");
    } catch (error) {
      console.error("Error exporting DOCX:", error);
      showErrorToast(`Erreur lors de l'export Word: ${error.message}`);
    }
  };

  // DOCX Export Handler (English)
  const handleExportDocxEn = async () => {
    try {
      const visibleSources = sourcesRisque.filter((source) =>
        selectedCategories.includes(source.category),
      );

      const categoryDistribution = {};
      visibleSources.forEach((source) => {
        const category = source.category || "non-categorise";
        categoryDistribution[category] =
          (categoryDistribution[category] || 0) + 1;
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: visibleSources.length,
        selectedSources: visibleSources.filter((s) => s.selected).length,
        categoriesCount: Object.keys(categoryDistribution).length,
        categoryDistribution: categoryDistribution,
        sources: visibleSources,
      };

      const { generateSourcesRisqueDocx } =
        await import("../../../utils/sourcesRisqueDocxGenerator.js");

      await generateSourcesRisqueDocx(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      }, "en");

      showSuccessToast("Word Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting DOCX (EN):", error);
      showErrorToast(`Error exporting Word (EN): ${error.message}`);
    }
  };

  // CSV Export Handler (French)
  const handleExportCSV = async () => {
    try {
      const visibleSources = sourcesRisque.filter((source) =>
        selectedCategories.includes(source.category),
      );

      const reportData = {
        sources: visibleSources,
      };

      const { default: SourcesRisqueCsvGenerator } =
        await import("../../../utils/sourcesRisqueCsvGenerator.js");

      const csvGenerator = new SourcesRisqueCsvGenerator(reportData, "fr");
      csvGenerator.download();

      showSuccessToast("Rapport CSV généré avec succès");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      showErrorToast(`Erreur lors de l'export CSV: ${error.message}`);
    }
  };

  // CSV Export Handler (English)
  const handleExportCSVEn = async () => {
    try {
      const visibleSources = sourcesRisque.filter((source) =>
        selectedCategories.includes(source.category),
      );

      const reportData = {
        sources: visibleSources,
      };

      const { default: SourcesRisqueCsvGenerator } =
        await import("../../../utils/sourcesRisqueCsvGenerator.js");

      const csvGenerator = new SourcesRisqueCsvGenerator(reportData, "en");
      csvGenerator.download();

      showSuccessToast("CSV Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting CSV (EN):", error);
      showErrorToast(`Error exporting CSV (EN): ${error.message}`);
    }
  };

  return (
    <div ref={formChangeRef} className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Left Side: Breadcrumb & Title */}
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span className="hover:text-blue-600 transition-colors">
                {t("workshop2.title")}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mx-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="text-blue-600 font-medium">
                {t("workshop2.activity1.breadcrumb")}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
              <AlertCircle size={28} className="mr-3 text-blue-600" />
              {t("workshop2.activity1.title")}
            </h1>
          </div>

          {/* Right Side: Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Consolidated Export Dropdown */}
            <WorkshopExportMenu
              onExportPdfFr={handleExportPDF}
              onExportPdfEn={handleExportPDFEn}
              onExportDocxFr={handleExportDocx}
              onExportDocxEn={handleExportDocxEn}
              onExportCsvFr={handleExportCSV}
              onExportCsvEn={handleExportCSVEn}
            />

            {/* Save Button */}
            <Button
              variant="primary"
              color="blue"
              icon={<Save />}
              onClick={handleSave}
              disabled={analysisStore.isWorkshopDataLoading}
              title={t("common.save")}
            >
              {t("common.save")}
            </Button>

            {/* Help Button */}
            <Button
              variant="secondary"
              color="white"
              icon={<Info />}
              onClick={() => setIsGuideOpen(true)}
              title={t("common.help")}
            >
              {t("common.help")}
            </Button>

            {/* AI Assistant Button */}
            {companyAiEnabled && (
            <Button
              variant="primary"
              color="indigo"
              icon={<BrainCircuit />}
              onClick={() => handleGetAiSuggestions(false)}
              disabled={!currentAnalysis?.id}
              loading={isLoadingAi && !showAiSuggestionsInline}
              title={t("common.aiAssistant", "Assistant ACG")}
            />
            )}
          </div>
        </div>
      </div>

      {/* Guide Modal - positioned right under the header */}
      <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      {/* AI Suggestions Inline */}
      <InlineAiSuggestions
        isVisible={showAiSuggestionsInline}
        onClose={() => setShowAiSuggestionsInline(false)}
        suggestions={aiSuggestions}
        isLoading={isLoadingAi}
        error={null} // You can pass an error state here if you create one
        onAddSelected={handleAddAiSuggestions}
        selectedSuggestions={selectedAiSuggestions}
        onToggleSelection={handleToggleAiSuggestion}
        onGetMore={handleGetMoreSuggestions}
      />

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-6">
        <div className="space-y-10">
          <ThreatCategories
            externalSelectedCategories={selectedCategories}
            setExternalSelectedCategories={setSelectedCategories}
          />

          {selectedCategories.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {t("workshop2.activity1.riskSourcesByCategory")}
              </h2>
              <CategorySourcesTable
                selectedCategories={selectedCategories}
                sourcesRisque={sourcesRisque}
                setSourcesRisque={(setterOrValue) => {
                  updateSourcesRisque(setterOrValue);
                  markModified();
                }}
                analysisData={currentAnalysis}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Activite1;
